import * as React from "react"

import { cn } from "../lib/utils"

interface FileInputProps extends Omit<React.ComponentProps<"input">, "type"> {
  /** Visible label rendered above the control. */
  label?: string
  /** Validation message; also flips the control to aria-invalid. */
  error?: string
  /** Hint copy shown when there is no error. */
  helperText?: string
}

/**
 * Native `<input type="file">` with the DS treatment on the browser's file
 * button. The DS `Input` is an `h-9` full-width text field and is the wrong
 * shape here, which is why file inputs were exempted from the bare-input lint
 * rule until this component existed.
 */
const FileInput = React.forwardRef<HTMLInputElement, FileInputProps>(
  ({ className, label, error, helperText, id, ...props }, ref) => {
    const generatedId = React.useId()
    const inputId = id || generatedId
    const messageId = error
      ? `${inputId}-error`
      : helperText
        ? `${inputId}-helper`
        : undefined

    const input = (
      <input
        type="file"
        id={inputId}
        ref={ref}
        aria-invalid={error ? true : undefined}
        aria-describedby={messageId}
        className={cn(
          "block w-full text-sm text-neutral-500",
          "file:mr-4 file:rounded-md file:border-0 file:px-4 file:py-2",
          "file:text-sm file:font-medium file:bg-primary-50 file:text-primary-700",
          "hover:file:bg-primary-100 file:cursor-pointer file:transition-colors",
          "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
          "disabled:cursor-not-allowed disabled:opacity-50",
          error && "text-destructive",
          className
        )}
        {...props}
      />
    )

    if (!label && !error && !helperText) {
      return input
    }

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={inputId}
            className="mb-1 block text-sm font-medium text-neutral-700"
          >
            {label}
          </label>
        )}
        {input}
        {error && (
          <p id={messageId} className="mt-1 text-sm text-destructive">
            {error}
          </p>
        )}
        {helperText && !error && (
          <p id={messageId} className="mt-1 text-sm text-muted-foreground">
            {helperText}
          </p>
        )}
      </div>
    )
  }
)
FileInput.displayName = "FileInput"

export { FileInput }
export type { FileInputProps }
